import { create } from 'zustand';
import type { CallSessionId, IdentityId, PublicKey } from '@shared/types';
import { reduceCallState, type CallStateAction, type CallStateSnapshot } from '../call/callStateReducer';

export type CallStatus =
  | 'idle'
  | 'initiating'
  | 'ringing'
  | 'connecting'
  | 'active'
  | 'ending'
  | 'handoff';

export type CallConnectionPhase =
  | 'preparing'
  | 'ringing'
  | 'answer_received'
  | 'verifying'
  | 'negotiating'
  | 'finding_route'
  | 'connecting_media'
  | 'reconnecting';

export interface IncomingCall {
  callSessionId: CallSessionId;
  fromIdentityId: IdentityId;
  localIdentityId?: IdentityId;
  localServerId?: string;
  localServerUrl?: string;
  isTemporaryLinkCall?: boolean;
  fromDisplayName?: string;
  fromIdentityName?: string;
  isKnownContact?: boolean;
  fromIdentityPublicKey?: PublicKey;
  e2eeVerified?: boolean;
  offer: RTCSessionDescriptionInit & {
    dtlsSignature?: string;
  };
}

export interface ActiveCall {
  callSessionId: CallSessionId;
  remoteIdentityId: IdentityId;
  localIdentityId?: string;
  serverId?: string;
  serverName?: string;
  serverUrl?: string;
  isKnownContact?: boolean;
  isTemporaryLinkCall?: boolean;
  remoteDisplayName?: string;
  remoteIdentityName?: string;
  remoteIdentityPublicKey?: PublicKey;
  status: CallStatus;
  connectionPhase?: CallConnectionPhase;
  isInitiator: boolean;
  startedAt?: Date;
  videoEnabled: boolean;
  audioEnabled: boolean;
  nativeState?: string;
  nativeRuntimeMode?: string;
  localVideoEnabled?: boolean;
  remoteVideoAvailable?: boolean;
  preparationError?: string;
  // E2EE security verification status
  securityVerified?: boolean;
}

type CallEndInfo = {
  callSessionId: CallSessionId;
  reason: string;
  source: 'ws' | 'webrtc' | 'user' | 'security' | 'unknown';
  at: number;
};

export interface CallState {
  // Current active call
  activeCall: ActiveCall | null;

  // Incoming call (if not answered yet)
  incomingCall: IncomingCall | null;

  // Connection state
  connectionState: RTCPeerConnectionState | null;

  // Debug: last known reason why a call ended.
  lastCallEndInfo: CallEndInfo | null;

  // UI state for switching between full-screen call and app screens.
  isCallMinimized: boolean;

  // Actions
  setActiveCall: (call: ActiveCall | null) => void;
  setIncomingCall: (call: IncomingCall | null) => void;
  setConnectionState: (state: RTCPeerConnectionState) => void;
  setLastCallEndInfo: (info: CallEndInfo) => void;
  setCallMinimized: (value: boolean) => void;
  minimizeCall: () => void;
  restoreCall: () => void;
  updateCallStatus: (status: CallStatus) => void;
  toggleVideo: () => void;
  toggleAudio: () => void;
  setVideoEnabled: (enabled: boolean) => void;
  setAudioEnabled: (enabled: boolean) => void;
  setNativeState: (nativeState: string | undefined) => void;
  dispatchCallState: (action: CallStateAction) => void;
  endCall: () => void;
}

export const useCallStore = create<CallState>((set) => ({
  activeCall: null,
  incomingCall: null,
  connectionState: null,
  lastCallEndInfo: null,
  isCallMinimized: false,

  setActiveCall: (call) => set((state) => reduceCallState(state as CallStateSnapshot, {
    type: 'set-active-call',
    call
  })),

  setIncomingCall: (call) => set((state) => reduceCallState(state as CallStateSnapshot, {
    type: 'set-incoming-call',
    call
  })),

  setConnectionState: (state) => set((current) => reduceCallState(current as CallStateSnapshot, {
    type: 'set-connection-state',
    state
  })),

  setLastCallEndInfo: (info) => set({ lastCallEndInfo: info }),

  setCallMinimized: (value) => set({ isCallMinimized: value }),
  minimizeCall: () => set({ isCallMinimized: true }),
  restoreCall: () => set({ isCallMinimized: false }),

  updateCallStatus: (status) => set((state) => reduceCallState(state as CallStateSnapshot, {
    type: 'update-call-status',
    status
  })),

  toggleVideo: () => set((state) => ({
    activeCall: state.activeCall
      ? { ...state.activeCall, videoEnabled: !state.activeCall.videoEnabled }
      : null
  })),

  toggleAudio: () => set((state) => ({
    activeCall: state.activeCall
      ? { ...state.activeCall, audioEnabled: !state.activeCall.audioEnabled }
      : null
  })),

  setVideoEnabled: (enabled) => set((state) => ({
    activeCall: state.activeCall
      ? { ...state.activeCall, videoEnabled: enabled }
      : null
  })),

  setAudioEnabled: (enabled) => set((state) => ({
    activeCall: state.activeCall
      ? { ...state.activeCall, audioEnabled: enabled }
      : null
  })),

  setNativeState: (nativeState) => set((state) => ({
    activeCall: state.activeCall
      ? { ...state.activeCall, nativeState }
      : null
  })),

  dispatchCallState: (action) => set((state) => reduceCallState(state as CallStateSnapshot, action)),

  endCall: () => set((state) => reduceCallState(state as CallStateSnapshot, { type: 'end-call' }))
}));

// Selectors
export const selectActiveCall = (state: CallState) => state.activeCall;
export const selectIncomingCall = (state: CallState) => state.incomingCall;
